import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip, type TooltipProps } from 'antd';
import { useSelector } from '../../util/store/store';

interface OperationMetricsTooltipProps {
    arrow?: boolean
    maxWidth?: number
    placement?: TooltipProps['placement']
}

const formatThreshold = (value?: number) =>
    typeof value === 'number' ? `${Math.round(value * 100) / 100}%` : '-';

const OperationMetricsTooltip = ({
    arrow = true,
    maxWidth = 380,
    placement = 'left',
}: OperationMetricsTooltipProps) => {
    const liceReductionThreshold = useSelector(
        state => state.login.user?.tenant.threshold_lice_reduction,
    );
    const mortalityIncreaseThreshold = useSelector(
        state => state.login.user?.tenant.threshold_mortality_increase,
    );
    const liceThresholds = [
        liceReductionThreshold?.stationary_lice,
        liceReductionThreshold?.mobile_lice,
        liceReductionThreshold?.female_lice,
    ].filter((value): value is number => typeof value === 'number');
    const averageLiceReductionThreshold = liceThresholds.length > 0
        ? liceThresholds.reduce((total, value) => total + value, 0) / liceThresholds.length
        : undefined;
    const liceReductionThresholdLabel = formatThreshold(averageLiceReductionThreshold);
    const mortalityIncreaseThresholdLabel = formatThreshold(mortalityIncreaseThreshold);

    return <Tooltip
        arrow={arrow ? { pointAtCenter: true } : false}
        placement={placement}
        autoAdjustOverflow={{ adjustX: 1, adjustY: 1 }}
        color='var(--bgSection)'
        styles={{
            root: {
                maxWidth: `min(${maxWidth}px, calc(100vw - 32px))`,
            },
            container: {
                maxHeight: 'calc(100vh - 64px)',
                overflowY: 'auto',
            },
        }}
        title={
            <div className='px-[8px] py-[8px] !text-[var(--colorSub)] [&_*]:!text-[var(--colorSub)] text-[15px]'>
                <div className='mb-[15px] font-[600] uppercase text-[12px]'>
                    Lice reduction, mortality increase at operation level
                </div>
                <div className='mb-[15px]'>
                    The metrics represent lice reduction (LR), mortality increase (MI) at the operation level.
                    Calculated as the average of the corresponding unit-level metrics across all units in the treatment.
                </div>

                <div className='mb-[10px] font-[600] uppercase'>Treatment grading status</div>
                <ul className='mb-[10px] list-disc space-y-[3px] pl-[25px]'>
                    <li>Avg. lice reduction across all treatments in past year: <strong>{liceReductionThresholdLabel}</strong></li>
                    <li>Avg. mortality increase across all treatments in past year: <strong>{mortalityIncreaseThresholdLabel}</strong></li>
                </ul>

                <div>Based on above Avg. lice reduction and Avg.</div>
                <div>Mortality increase system will assign a treatment grading status as follow:</div>
                <ul className='mt-[3px] list-disc space-y-[15px] pl-[25px]'>
                    <li>
                        <div className='font-[600] [&&]:!text-[#5DACA5]'>Excellent</div>
                        <div className='italic'>(LR &gt; {liceReductionThresholdLabel}, MI &lt;=0)</div>
                    </li>
                    <li>
                        <div className='font-[600] [&&]:!text-[#72A64E]'>Good</div>
                        <div className='italic'>(LR &gt; {liceReductionThresholdLabel} &amp; MI &lt; {mortalityIncreaseThresholdLabel})</div>
                        <div className='italic'>OR (LR &lt; {liceReductionThresholdLabel} &amp; MI &lt;=0)</div>
                    </li>
                    <li>
                        <div className='font-[600] [&&]:!text-[#677979]'>Acceptable</div>
                        <div className='italic'>(LR &lt; {liceReductionThresholdLabel} &amp; MI &lt; {mortalityIncreaseThresholdLabel})</div>
                        <div className='italic'>OR (LR &gt; {liceReductionThresholdLabel} &amp; MI &gt; {mortalityIncreaseThresholdLabel})</div>
                        <div className='italic'>OR (LR &gt;=0 &amp; MI &lt;=0)</div>
                    </li>
                    <li>
                        <div className='font-[600] [&&]:!text-[#F69C6F]'>Poor</div>
                        <div className='italic'>(When LR &lt; {liceReductionThresholdLabel} &amp; MI &gt; {mortalityIncreaseThresholdLabel})</div>
                        <div className='italic'>OR (LR &lt;=0 &amp; MI &lt; {mortalityIncreaseThresholdLabel})</div>
                    </li>
                    <li>
                        <div className='font-[600] [&&]:!text-[#E95D5D]'>Very poor</div>
                        <div className='italic'>(LR &gt;=0 &amp; MI &gt; {mortalityIncreaseThresholdLabel})</div>
                    </li>
                </ul>
            </div>
        }
    >
        <InfoCircleOutlined className='cursor-help text-[18px] text-[var(--colorSub)]' />
    </Tooltip>;
};

export default OperationMetricsTooltip;
